<template>
    <div>
        <h3 class="mb-4" v-if="enabledAttachments.length > 0">
            Přílohy, které jsou součástí odhadu
        </h3>

        <draggable v-model="enabledAttachments" handle=".card-header" @end="onDragEnd" :item-key="(attachmentType: AttachmentType) => attachmentType">
            <template #item="{element: attachmentType}">
            <div class="card shadow mb-3"
                 :id="'attachment-type-' + attachmentType">
                <attachment-type-control
                    :attachment-type="attachmentType"
                    :checked="true"
                    :attachments="attachmentsByType(attachmentType)"
                    @changed="refresh"
                />
            </div>
            </template>
        </draggable>

        <h3 class="my-4" v-if="disabledAttachments.length > 0 && enabledAttachments.length > 0">
            Přílohy, které nejsou součástí odhadu
        </h3>

        <div class="card shadow mb-3" v-for="attachmentType in disabledAttachments" :key="attachmentType">
            <attachment-type-control
                :attachment-type="attachmentType"
                :checked="false"
                :attachments="attachmentsByType(attachmentType)"
                @changed="refresh"
            />
        </div>
    </div>
</template>

<script lang="ts">
import AttachmentTypeControl from '@/components/estimates/expertEstimate/AttachmentTypeControl.vue';
import {Segment} from '@/composables/Segment';
import {Attachment, AttachmentType, ExpertEstimateState as EstimateState} from '@/model/api';
import {Estimate} from '@/model/Estimate';
import {right} from 'fp-ts/lib/Either';
import {defineComponent, inject, nextTick, PropType, ref} from 'vue';
import draggable from 'zhyswan-vuedraggable';

export default defineComponent({
    components: {draggable, AttachmentTypeControl},
    props: {
        availableAttachmentTypes: {
            type: Array as PropType<AttachmentType[]>,
            required: true,
        },
        enabledAttachmentTypes: {
            type: Array as PropType<AttachmentType[]>,
            required: true,
        },
        attachments: {
            type: Array as PropType<Attachment[]>,
            required: true,
        },
        estimate: {
            type: Object as PropType<EstimateState>,
            required: true,
        },
    },
    setup(props, {expose}) {
        const model = inject<Estimate<EstimateState>>('model')!;

        const estimateState = ref<EstimateState>(props.estimate);
        const enabledAttachments = ref<AttachmentType[]>(props.estimate.enabledAttachmentTypes);
        const disabledAttachments = ref<AttachmentType[]>(
            props.availableAttachmentTypes.filter(
                type => !enabledAttachments.value.includes(type),
            ),
        );

        function attachmentsByType(type: AttachmentType): Attachment[] {
            return props.attachments.filter(attachment => attachment.type === type);
        }

        function scrollToAttachment(attachmentType: AttachmentType): void {
            nextTick(() => {
                const element = document.getElementById('attachment-type-' + attachmentType);
                if (element) {
                    element.scrollIntoView({behavior: 'smooth', block: 'center'});
                }
            });
        }

        function refresh(type?: AttachmentType): void {
            model.getEstimate()
                .then(estimate => {
                    estimateState.value = estimate;
                    enabledAttachments.value = estimate.enabledAttachmentTypes;
                    disabledAttachments.value = props.availableAttachmentTypes.filter(
                        attachmentType => !enabledAttachments.value.includes(attachmentType),
                    );

                    if (type !== undefined) {
                        scrollToAttachment(type);
                    }
                });
        }

        function onDragEnd(event: {newIndex: number}): void {
            if (model.changeAttachmentPosition === undefined) {
                throw new Error('Estimate does not support attachment position change');
            }

            const position = event.newIndex;
            const attachmentType = enabledAttachments.value[position];

            model.changeAttachmentPosition(attachmentType, position)
                .then(() => refresh());
        }

        async function submit() {
            return right(estimateState.value);
        }

        function hasNotSubmittedChanges(): boolean {
            return false; // this segment does not use submit button
        }

        function clearNotSubmittedChanges(): void {
        }

        function allowsMovingOn(): boolean {
            return true;
        }

        function beforeEnter(): void {
        }

        const segmentApi: Segment<EstimateState> = {
            hasNotSubmittedChanges,
            clearNotSubmittedChanges,
            submit,
            allowsMovingOn,
            beforeEnter,
        };

        expose(segmentApi);

        return {
            enabledAttachments,
            disabledAttachments,
            attachmentsByType,
            onDragEnd,
            refresh,
            ...segmentApi,
        };
    },
});
</script>
